import axios, { AxiosInstance, CreateAxiosDefaults } from "axios";
import {
  getAuthSessionFromStorage,
  getEnvironmentVariableByName,
  getUinfoTokenFromStorage,
} from "../utils";

const PORT = getEnvironmentVariableByName("DEV_SERVER_PORT");

const isLocal = window.getEnv() === "local";
// reading environment
const apiBasePath = getEnvironmentVariableByName("API_BASE_PATH");
const apiOnfidoBasePath = getEnvironmentVariableByName(
  "API_PROCESS_ONFIDO_BASE_PATH",
);
const apiResumeBasePath = getEnvironmentVariableByName(
  "API_PROCESS_RESUME_BASE_PATH",
);
const apiStatusBasePath = getEnvironmentVariableByName(
  "API_PROCESS_STATUS_BASE_PATH",
);
const apiCreatePasswordBasePath = getEnvironmentVariableByName("API_PROCESS_C");
const apiCMSBasePath = getEnvironmentVariableByName(
  "API_PROCESS_CMS_BASE_PATH",
);
const apiDataBasePath = getEnvironmentVariableByName(
  "API_SERVICES_DATA_BASE_PATH",
);

// build up paths.
export const baseOnboardingApiURL = isLocal
  ? `http://localhost:${PORT}/ob-api-base`
  : apiBasePath;

export const baseOnfidoApiURL = isLocal
  ? `http://localhost:${PORT}/ob-api-onfido`
  : apiOnfidoBasePath;
export const baseResumeApiURL = isLocal
  ? `http://localhost:${PORT}/ob-api-resume`
  : apiResumeBasePath;
export const baseStatusApiURL = isLocal
  ? `http://localhost:${PORT}/ob-api-status`
  : apiStatusBasePath;
export const baseCMSApiURL = isLocal
  ? `http://localhost:${PORT}/ob-api-cms`
  : apiCMSBasePath;
export const baseDataApiURL = isLocal
  ? `http://localhost:${PORT}/ob-api-data`
  : apiDataBasePath;

export const apiOnboardingResourcePath =
  getEnvironmentVariableByName("API_RESOURCE_PATH");
export const apiOnboardingVersion = getEnvironmentVariableByName("API_VERSION");
export const apiOnboardingProcess = getEnvironmentVariableByName("API_PROCESS");
export const apiCreatePasswordProcess = getEnvironmentVariableByName(
  "API_CREATE_PASSWORD",
);
export const apiOnboardingProcessOnfido =
  getEnvironmentVariableByName("API_PROCESS_ONFIDO");
export const apiOnboardingResume =
  getEnvironmentVariableByName("API_PROCESS_RESUME");
export const apiOnboardingStatus =
  getEnvironmentVariableByName("API_PROCESS_STATUS");

export const apiServiceResourcePath = getEnvironmentVariableByName(
  "API_SERVICES_RESOURCE_PATH",
);
export const remoteThemeName =
  getEnvironmentVariableByName("REMOTE_THEME_NAME");

export const apiServiceVersion = getEnvironmentVariableByName(
  "API_SERVICES_RESOURCE_VERSION",
);

export const apiServiceRegistry = getEnvironmentVariableByName(
  "API_SERVICES_REGISTRY",
);

export const apiServiceData = getEnvironmentVariableByName("API_SERVICES_DATA");

// ONBOARDING APIs
export const apiOnboardingKeyName = getEnvironmentVariableByName(
  "API_ONBOARDING_KEY_NAME",
);
export const apiOnboardingKeyValue = getEnvironmentVariableByName(
  "API_ONBOARDING_KEY_VALUE",
);
export const apiProcessCms = getEnvironmentVariableByName("API_PROCESS_CMS");
// SERVICES APIs
export const apiFacadeKeyName = getEnvironmentVariableByName(
  "API_FACADE_KEY_NAME",
);

export const apiFacadeKeyValue = getEnvironmentVariableByName(
  "API_FACADE_KEY_VALUE",
);

// DATA APIs
export const apiDataKeyName = getEnvironmentVariableByName("API_DATA_KEY_NAME");
export const apiDataKeyValue =
  getEnvironmentVariableByName("API_DATA_KEY_VALUE");

export const API_SERVICE_KEYS = {
  [apiDataKeyName]: apiDataKeyValue,
  [apiFacadeKeyName]: apiFacadeKeyValue,
  [apiOnboardingKeyName]: apiOnboardingKeyValue,
};

export const endpoints = {
  base: `${baseOnboardingApiURL}`,
  document: `${baseOnboardingApiURL}${apiOnboardingResourcePath}${apiServiceVersion}/document`,
  generateOtp: `${baseOnboardingApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiOnboardingProcess}`,
  onboarding: `${baseOnboardingApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiOnboardingProcess}`,
  createPassword: `${baseOnboardingApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiCreatePasswordProcess}`,
  resume: `${baseResumeApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiOnboardingResume}`,
  status: `${baseStatusApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiOnboardingStatus}`,
  services: `${baseOnboardingApiURL}${apiServiceResourcePath}${apiServiceVersion}${apiServiceRegistry}`,
  data: `${baseDataApiURL}${apiServiceResourcePath}${apiServiceVersion}${apiServiceData}`,
  theme: `${baseCMSApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiProcessCms}`,
  onfidoBase: `${baseOnfidoApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiOnboardingProcessOnfido}`,
  onfido: {
    init: `${baseOnfidoApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiOnboardingProcessOnfido}/init`,
    save: `${baseOnfidoApiURL}${apiOnboardingResourcePath}${apiOnboardingVersion}${apiOnboardingProcessOnfido}/save`,
  },
};

export const buildThemePath = (themeName?: string | null) =>
  `${endpoints.theme}/${themeName ?? remoteThemeName}`;

export const getOnfidoFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
  addAuthToken?: boolean,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: endpoints.onfidoBase,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiOnboardingKeyName] =
      apiOnboardingKeyValue;
    if (addAuthToken) {
      const authToken = getAuthSessionFromStorage();
      if (authToken) {
        (config.headers as Record<string, unknown>)["auth-token"] = authToken;
      }
      const uinfoToken = getUinfoTokenFromStorage();
      if (uinfoToken) {
        (config.headers as Record<string, unknown>)["uinfo"] = uinfoToken;
      }
    }
    return config;
  });
  return instance;
};

const getOnboardingFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
  addAuthToken?: boolean,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: endpoints.onboarding,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiOnboardingKeyName] =
      apiOnboardingKeyValue;
    if (addAuthToken) {
      const authToken = getAuthSessionFromStorage();
      if (authToken) {
        (config.headers as Record<string, unknown>)["auth-token"] = authToken;
      }
      const uinfoToken = getUinfoTokenFromStorage();
      if (uinfoToken) {
        (config.headers as Record<string, unknown>)["uinfo"] = uinfoToken;
      }
    }
    return config;
  });
  return instance;
};

const getCreatePasswordFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
  addAuthToken?: boolean,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: endpoints.createPassword,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiOnboardingKeyName] =
      apiOnboardingKeyValue;
    if (addAuthToken) {
      const authToken = getAuthSessionFromStorage();
      if (authToken) {
        (config.headers as Record<string, unknown>)["auth-token"] = authToken;
      }
      const uinfoToken = getUinfoTokenFromStorage();
      if (uinfoToken) {
        (config.headers as Record<string, unknown>)["uinfo"] = uinfoToken;
      }
    }
    return config;
  });
  return instance;
};

const getResumeFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
  addAuthToken?: boolean,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: endpoints.resume,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiOnboardingKeyName] =
      apiOnboardingKeyValue;
    if (addAuthToken) {
      const authToken = getAuthSessionFromStorage();
      if (authToken) {
        (config.headers as Record<string, unknown>)["auth-token"] = authToken;
      }
    }
    return config;
  });
  return instance;
};

const getStatusFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: endpoints.status,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiOnboardingKeyName] =
      apiOnboardingKeyValue;
    return config;
  });
  return instance;
};

const getServiceFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: endpoints.services,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiFacadeKeyName] =
      apiFacadeKeyValue;
    return config;
  });
  return instance;
};

const getDownloadFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: "",
    withCredentials: true,
    responseType: "arraybuffer",
    headers: {
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiOnboardingKeyName] =
      apiOnboardingKeyValue;
    return config;
  });
  return instance;
};
const getDataFetcher = (
  headers?: Record<string, unknown>,
  axiosConfig?: CreateAxiosDefaults,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: endpoints.data,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...axiosConfig,
  });
  instance.interceptors.request.use(async config => {
    (config.headers as Record<string, unknown>)[apiDataKeyName] =
      apiDataKeyValue;
    return config;
  });
  return instance;
};

export {
  getDataFetcher,
  getDownloadFetcher,
  getOnboardingFetcher,
  getResumeFetcher,
  getServiceFetcher,
  getStatusFetcher,
  getCreatePasswordFetcher,
};
